import {UnitTypeEntry} from './UnitTypeEntry';
import {Directive, Injectable, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ReadOnlyState, ReadOnlyStateManager} from '../../../project-details/ReadOnlyStateManager';
import {ProjectDetailsTab} from '../../../project-details/ProjectDetailsTab';
import {takeUntil} from 'rxjs/operators';
import {CalcUtil} from '../../CalcUtil';
import {Subject} from 'rxjs';
import {FinancialModelReadManager} from '../../../project-details/FinancialModelReadManager';

@Directive()
@Injectable()
export class AbstractUnits implements OnDestroy, OnChanges {
  destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() readOnlyStateManager: ReadOnlyStateManager;
  @Input() financialModelReadManager: FinancialModelReadManager;
  @Input() formGroup: FormGroup;
  @Input() initialData: any;
  @Input() units: UnitTypeEntry[];
  @Input() projectCreation: boolean;
  readOnlyState: ReadOnlyState;

  protected constructor(private tab: ProjectDetailsTab, private capacityFactor: number) {}

  ngOnDestroy() {
    this.destroyUnitFgSubscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.refreshUnitTypes();
    this.updateFormGroupAndInitialData();
    if (changes.readOnlyStateManager) {
      this.readOnlyState = this.readOnlyStateManager?.of([
        ProjectDetailsTab.technical, this.tab
      ]);
    }
    if (changes.units) {
      this.destroyUnitFgSubscription();
      this.destroy$ = new Subject<boolean>();
      for (const fg of this.units.map(unit => unit.fg)) {
        this.addTotalCapacityCalculationToUnitFg(fg);
      }
    }
  }

  private addTotalCapacityCalculationToUnitFg(fg: FormGroup) {
    let prevCount = fg.value.count;
    let prevCapacity = fg.value.capacity;
    let updating = false;
    fg.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(fgValue => {
        if (!updating && (fgValue.count !== prevCount || fgValue.capacity !== prevCapacity)) {
          prevCount = fgValue.count;
          prevCapacity = fgValue.capacity;

          const totalCapacity = CalcUtil.calculateTotalCapacity(fgValue.count, fgValue.capacity, this.capacityFactor);
          updating = true;
          fg.patchValue({totalCapacity});
          updating = false;
        }
      });
  }

  private destroyUnitFgSubscription() {
    if (this.destroy$) {
      this.destroy$.next(true);
      this.destroy$.unsubscribe();
    }
  }

  public isReadOnly(): boolean {
    return this.readOnlyStateManager?.isReadOnly(ProjectDetailsTab.technical, this.tab);
  }

  public onDelete(unit: UnitTypeEntry) {
    this.formGroup.removeControl(unit.id);
    unit.active = false;
    this.refreshUnitTypes();
  }

  public onAdd(unit: UnitTypeEntry) {
    this.formGroup.addControl(unit.id, unit.fg);
    unit.active = true;
    this.refreshUnitTypes();
  }

  protected refreshUnitTypes() {
    for (const unit of this.units) {
      unit.canAdd = false;
      unit.canDelete = false;
    }
    let firstInactiveIdx = this.units.findIndex(u => !u.active);
    if (firstInactiveIdx > 1) {
      this.units[firstInactiveIdx - 1].canDelete = true;
    }
    if (firstInactiveIdx != -1) {
      this.units[firstInactiveIdx].canAdd = true;
    } else {
      this.units[this.units.length - 1].canDelete = true;
    }
  }

  protected updateFormGroupAndInitialData() {
    for (const unit of this.units) {
      if (unit.active && !this.formGroup.contains(unit.id)) {
        this.formGroup.addControl(unit.id, unit.fg);
        if (this.initialData) {
          this.initialData[unit.id] = Object.assign({}, unit.fg.value);
        }
      }
      if (!unit.active && this.formGroup.contains(unit.id)) {
        this.formGroup.removeControl(unit.id);
        if (this.initialData) {
          delete this.initialData[unit.id];
        }
      }
    }
  }

}
